import { useEffect, useState } from "react";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple, Printer, Receipt } from "@phosphor-icons/react";

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

export default function Statements() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/statements/${year}`)
      .then(({ data }) => setSt(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [year]);

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/statements/${year}?format=csv`, { responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `tax_statement_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="statements-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Tax Records</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Year-end statement</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">
        A summary of everything you paid us, for your childcare tax credit or reimbursement claim.
      </p>

      <div className="mt-8 flex flex-wrap gap-3 items-end print:hidden">
        <div className="w-40">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-12 rounded-xl bg-white" data-testid="statement-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={download} disabled={busy}
                className="h-12 px-7 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                data-testid="statement-download-btn">
          <DownloadSimple size={18} className="mr-2" /> Download CSV
        </Button>
        <Button variant="outline" onClick={() => window.print()} className="h-12 px-7 rounded-full font-bold"
                data-testid="statement-print-btn">
          <Printer size={18} className="mr-2" /> Print
        </Button>
      </div>

      {!st ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : st.payment_count === 0 ? (
        <div className="mt-10 bg-white rounded-3xl border border-border p-12 text-center" data-testid="statement-empty">
          <Receipt size={56} weight="duotone" className="mx-auto text-[#3B82F6]" />
          <p className="mt-6 font-display text-xl font-semibold">No payments recorded for {year}</p>
          <p className="mt-2 text-muted-foreground">
            Once tuition payments are made they'll be itemised here for your records.
          </p>
        </div>
      ) : (
        <div className="mt-8 bg-white rounded-3xl border border-border p-8 max-w-3xl" data-testid="statement-body">
          <div className="flex flex-wrap justify-between gap-6 pb-6 border-b border-border">
            <div>
              <p className="font-display text-xl font-semibold">{st.program.name}</p>
              <p className="text-sm text-muted-foreground mt-1">{st.program.address}</p>
              <p className="text-sm text-muted-foreground">{st.program.phone} · {st.program.email}</p>
              {st.program.tax_id && <p className="text-sm text-muted-foreground">Tax ID: {st.program.tax_id}</p>}
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Statement year</p>
              <p className="font-display text-3xl font-bold">{st.year}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Paid by</p>
              <p className="mt-1 font-bold">{st.payer_name || st.payer_email}</p>
              <p className="text-sm text-muted-foreground">{st.payer_email}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Total paid</p>
              <p className="mt-1 font-display text-3xl font-bold text-[#10B981]" data-testid="statement-total">
                {money(st.total_paid)}
              </p>
            </div>
          </div>

          <h2 className="mt-8 font-display text-lg font-semibold">Paid per child</h2>
          <div className="mt-3 space-y-2">
            {Object.entries(st.by_child).map(([child, amt]) => (
              <div key={child} className="flex justify-between text-sm border-b border-border pb-2"
                   data-testid={`statement-child-${child.replace(/\s+/g, "-")}`}>
                <span className="font-semibold">{child}</span>
                <span className="font-bold">{money(amt)}</span>
              </div>
            ))}
          </div>

          <h2 className="mt-8 font-display text-lg font-semibold">
            Payments ({st.payment_count})
          </h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {["Date", "Invoice", "Child", "Description", "Amount", "Method"].map((h) => (
                    <th key={h} className="text-left p-3 text-xs font-bold uppercase tracking-[0.1em] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {st.rows.map((r, i) => (
                  <tr key={`${r.number}-${r.date}-${i}`} className="border-b border-border last:border-0"
                      data-testid={`statement-row-${i}`}>
                    <td className="p-3 whitespace-nowrap">{r.date}</td>
                    <td className="p-3 whitespace-nowrap">{r.number}</td>
                    <td className="p-3 whitespace-nowrap">{r.student}</td>
                    <td className="p-3">{r.description}</td>
                    <td className="p-3 font-bold whitespace-nowrap">{money(r.amount)}</td>
                    <td className="p-3 whitespace-nowrap capitalize">{r.method || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-8 text-xs text-muted-foreground">
            Keep this statement for your records. It reflects payments received by
            {" "}{st.program.name}. We are not able to give tax advice — please check with your tax
            preparer about which amounts you can claim.
          </p>
        </div>
      )}
    </div>
  );
}
